/**
 * Sentry SDK bootstrap (TASK-079 Phase 2 / FEAT-058).
 *
 * Single source of truth for Sentry init, PII redaction (`beforeSend` /
 * `beforeBreadcrumb`), and per-route trace sampling (`tracesSampler`).
 *
 * Public surface:
 *   - initSentry()              — Bootstraps the SDK; no-op when DSN unset.
 *   - beforeSend(event)         — Exported for unit testing.
 *   - beforeBreadcrumb(crumb)   — Exported for unit testing.
 *   - tracesSampler(ctx)        — Exported for unit testing.
 *   - setSentryUser(userId)     — Sets / clears the Cognito-sub user block.
 *
 * PII contract (non-negotiable per systemPatterns.md Observability Sub-Pattern):
 *   - User block: keep ONLY `id` (Cognito sub). Never email, name, opcoName.
 *   - Request bodies: scrub for /auth/*, /admin/users/*, /admin/communications/*.
 *   - Headers: drop Authorization and Cookie unconditionally.
 *   - Breadcrumbs: re-run scrub at both creation and send time.
 *   - Extras + contexts: redact any field whose key is in PII denylist.
 *
 * Sampling contract:
 *   - 0.0 for high-volume polling endpoints (no quota burn):
 *       /api/notifications/unread-count
 *       /api/admin/users/preferences/poll
 *       /api/version.json
 *   - Otherwise read VITE_SENTRY_TRACES_SAMPLE_RATE (default 1.0).
 *
 * Spec: memory-bank/creative/TASK-079-phase2-observability.md
 *       (Sampling & Redaction section)
 */

import * as Sentry from '@sentry/react';

// ---------------------------------------------------------------------------
// PII denylists — owned by this module (single source of truth).
// ---------------------------------------------------------------------------

const PII_USER_FIELDS = [
  'email',
  'firstName',
  'lastName',
  'name',
  'opcoName',
  'opcoSlug',
  'phone',
];

const SENSITIVE_REQUEST_PATHS = [
  /^\/auth\//,
  /^\/admin\/users\//,
  /^\/admin\/communications\//,
];

const NEVER_SAMPLE_PATHS = [
  '/api/notifications/unread-count',
  '/api/admin/users/preferences/poll',
  '/api/version.json',
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function redactObject(obj, fields) {
  if (!obj || typeof obj !== 'object') return obj;
  const copy = Array.isArray(obj) ? [...obj] : { ...obj };
  for (const f of fields) {
    if (f in copy) copy[f] = '[REDACTED]';
  }
  return copy;
}

function urlMatchesAny(url, regexes) {
  if (!url) return false;
  return regexes.some((re) => re.test(url));
}

// ---------------------------------------------------------------------------
// beforeSend — final gate before any event hits the Sentry transport.
// ---------------------------------------------------------------------------

export function beforeSend(event) {
  // 1. User block — keep ONLY userId (Cognito sub).
  if (event.user) {
    event.user = { id: event.user.id ?? event.user.userId };
  }

  // 2. Tags — strip any tags whose key matches the denylist.
  if (event.tags) {
    for (const k of Object.keys(event.tags)) {
      if (PII_USER_FIELDS.includes(k)) delete event.tags[k];
    }
  }

  // 3. Request data — scrub bodies for sensitive paths and headers always.
  if (event.request) {
    if (event.request.headers) {
      // Authorization may carry a bearer token; remove unconditionally.
      delete event.request.headers.Authorization;
      delete event.request.headers.authorization;
      delete event.request.headers.Cookie;
      delete event.request.headers.cookie;
    }
    const url = event.request.url ?? '';
    if (urlMatchesAny(url, SENSITIVE_REQUEST_PATHS)) {
      event.request.data = '[REDACTED:sensitive-path]';
      if (event.request.query_string) event.request.query_string = '[REDACTED]';
    }
  }

  // 4. Breadcrumbs — re-run the scrub on each fetch/xhr breadcrumb.
  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.map((crumb) => {
      if (crumb.category === 'fetch' || crumb.category === 'xhr') {
        const u = crumb.data?.url ?? '';
        if (urlMatchesAny(u, SENSITIVE_REQUEST_PATHS)) {
          return {
            ...crumb,
            data: { ...crumb.data, body: '[REDACTED]', input: '[REDACTED]' },
          };
        }
      }
      return crumb;
    });
  }

  // 5. Extras + contexts — defensive scrub for any field whose name is in the
  // PII denylist (covers manual `Sentry.setExtra`, contexts.user, etc.).
  if (event.extra) event.extra = redactObject(event.extra, PII_USER_FIELDS);
  if (event.contexts?.user) {
    event.contexts.user = redactObject(event.contexts.user, PII_USER_FIELDS);
  }

  return event;
}

// ---------------------------------------------------------------------------
// beforeBreadcrumb — defense in depth: scrub at creation time too, so even
// if `beforeSend` is skipped (in-memory event store, replay, etc.) we never
// retain raw bodies for sensitive paths.
// ---------------------------------------------------------------------------

export function beforeBreadcrumb(crumb, hint) {
  if (crumb.category === 'fetch' || crumb.category === 'xhr') {
    const u = crumb.data?.url ?? hint?.input?.[0] ?? '';
    if (urlMatchesAny(u, SENSITIVE_REQUEST_PATHS)) {
      return {
        ...crumb,
        data: { ...crumb.data, body: '[REDACTED]', input: '[REDACTED]' },
      };
    }
  }
  return crumb;
}

// ---------------------------------------------------------------------------
// tracesSampler — per-route sampling decision.
// ---------------------------------------------------------------------------

export function tracesSampler(samplingContext) {
  const url =
    samplingContext?.transactionContext?.attributes?.['http.url'] ??
    samplingContext?.location?.pathname ??
    '';

  for (const path of NEVER_SAMPLE_PATHS) {
    if (url.includes(path)) return 0.0;
  }

  const raw = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '1.0';
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 1.0;
}

// ---------------------------------------------------------------------------
// initSentry — bootstrap. No-op when DSN is empty (e.g. local dev / PR builds).
// ---------------------------------------------------------------------------

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  // `__BUILD_VERSION__` is a Vite `define` (see frontend/vite.config.js).
  // Guard with typeof for tests where the global isn't injected.
  const release =
    typeof __BUILD_VERSION__ !== 'undefined' ? __BUILD_VERSION__ : undefined;

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    release,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampler,
    beforeSend,
    beforeBreadcrumb,
  });
}

// ---------------------------------------------------------------------------
// setSentryUser — only Cognito sub permitted on the user block.
// ---------------------------------------------------------------------------

export function setSentryUser(userId) {
  if (userId) {
    Sentry.setUser({ id: userId });
  } else {
    Sentry.setUser(null);
  }
}
